import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于韩漫网 - 韩漫在线免费阅读平台',
  description: '韩漫网是专注韩国漫画的中文在线阅读导航平台，提供海量优质韩漫资源，支持免费在线阅读，每日更新最新韩漫内容。',
  url: 'https://www.hanmanwang.com/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '韩漫网',
    url: 'https://www.hanmanwang.com',
  },
};

const reputationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '韩漫网',
  url: 'https://www.hanmanwang.com',
  description: '韩漫网 - 专注于韩国漫画的中文在线阅读导航平台',
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    reviewCount: '12580',
    bestRating: '5',
  },
};

const contactJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: '联系韩漫网',
  url: 'https://www.hanmanwang.com/about',
  mainEntity: {
    '@type': 'Organization',
    name: '韩漫网',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@hanmanwang.com',
    },
  },
};

const teamMembers = [
  { name: '李志勋', role: '创始人 & CEO', desc: '韩漫爱好者，致力于打造中文圈最优质的韩漫阅读平台。' },
  { name: '金美英', role: '内容总监', desc: '资深韩漫编辑，负责漫画内容筛选与质量控制，确保每一部作品都精彩。' },
  { name: '朴俊秀', role: '技术总监', desc: '全栈工程师，负责平台技术架构与用户体验优化，保障流畅的阅读体验。' },
  { name: '崔恩智', role: '运营经理', desc: '负责社区运营与用户服务，倾听每一位读者的声音，持续优化服务。' },
];

const milestones = [
  { year: '2023', title: '平台创立', desc: '韩漫网正式上线，开始为中文读者提供韩漫在线阅读服务。' },
  { year: '2024', title: '快速成长', desc: '收录漫画超过3000部，月活跃用户突破50万，成为中文韩漫领域重要平台。' },
  { year: '2025', title: '体验升级', desc: '全面优化阅读体验，新增下拉式阅读模式，用户满意度大幅提升。' },
  { year: '2026', title: '蓬勃发展', desc: '收录漫画超过5000部，注册用户突破180万，每日更新超过200部作品。' },
];

export default function AboutPage() {
  return (
    <>
      <SEOMeta
        title="关于韩漫网 - 品牌介绍"
        description="韩漫网是专注韩国漫画的中文在线阅读导航平台，提供海量优质韩漫资源，支持免费在线阅读。了解韩漫网品牌故事、团队介绍、用户口碑及联系方式。"
        keywords="韩漫网关于,韩漫网介绍,韩漫网联系方式,韩漫网品牌,韩漫在线阅读平台"
        canonicalUrl="https://www.hanmanwang.com/about"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(reputationJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(contactJsonLd) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-4">关于韩漫网</h1>
              <p className="mt-2 text-sm text-foreground-600">了解韩漫网的品牌故事与发展历程</p>
            </div>
          </div>

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="lg:w-1/2">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">品牌介绍</h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    韩漫网创建于2023年，是专注于韩国漫画的中文在线阅读导航平台。我们的使命是为中文读者提供最便捷、最全面的韩漫阅读体验，让每一位韩漫爱好者都能轻松找到自己喜欢的作品。
                  </p>
                  <p>
                    经过数年的发展，韩漫网已经成为中文韩漫领域最具影响力的平台之一。我们收录了超过<strong>5000部</strong>优质韩漫作品，涵盖恋爱、奇幻、动作、悬疑、耽美、都市等多种题材，满足不同读者的阅读需求。
                  </p>
                  <p>
                    韩漫网坚持<strong>免费在线</strong>阅读模式，所有漫画均支持下拉式流畅阅读。我们的编辑团队每日精心筛选内容，确保每一部收录的作品都具备高品质的画风和精彩的故事。
                  </p>
                  <p>
                    我们深知读者对韩漫的热爱与期待，因此不断优化平台体验，致力成为华人世界最值得信赖的韩漫阅读首选平台。无论是追更热门连载，还是回顾经典完结作品，韩漫网都是您的最佳选择。
                  </p>
                </div>
              </div>
              <div className="lg:w-1/2">
                <div className="rounded-lg overflow-hidden bg-background-200 aspect-video">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20reading%20scene%20cozy%20modern%20room%20with%20bookshelves%20warm%20soft%20lighting%20person%20reading%20comic%20on%20tablet%20comfortable%20atmosphere%20clean%20aesthetic%20illustration%20style%20warm%20pastel%20colors&width=800&height=450&seq=about-hero-hanman&orientation=landscape"
                    alt="韩漫网 - 舒适阅读体验"
                    title="韩漫网品牌形象"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-8">发展历程</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {milestones.map((item) => (
                  <div key={item.year} className="text-center p-6 rounded-lg bg-background-50">
                    <span className="inline-block px-4 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold mb-3">
                      {item.year}
                    </span>
                    <h3 className="font-heading text-lg text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-8">用户口碑</h2>
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-accent-500 w-6 h-6 flex items-center justify-center text-2xl"></i>
                ))}
              </div>
              <p className="mt-2 text-3xl font-bold text-foreground-950 font-heading">4.8</p>
              <p className="text-sm text-foreground-500">基于 12,580 条用户评价</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: '小猫咪', text: '韩漫网真的是我用过最好用的韩漫平台！分类清楚，更新速度快，画质也超赞。每天必刷！' },
                { name: '漫画达人小李', text: '从韩漫网追了很多年了，资源全、更新快，下拉式阅读体验真的很流畅，推荐给所有韩漫爱好者。' },
                { name: '爱看漫画的小王', text: '韩漫网的分类做得特别好，找漫画很方便。完结韩漫专区是我的最爱，能一口气看完太爽了！' },
                { name: '深夜追漫人', text: '更新速度是真的快，韩漫网基本上韩国那边更新第二天就能看到中文版了，太给力了！' },
                { name: '甜甜圈', text: '恋爱韩漫资源特别丰富，画风都很好看。韩漫网的推荐算法也很准，总能找到喜欢的作品。' },
                { name: '热血少年', text: '动作和奇幻分类的韩漫质量都很高，韩漫网的筛选做得不错，没有乱七八糟的低质内容。' },
              ].map((review, i) => (
                <div key={i} className="p-5 rounded-lg bg-background-100">
                  <div className="flex items-center gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 w-3.5 h-3.5 flex items-center justify-center text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">{review.text}</p>
                  <p className="text-xs text-foreground-500 font-medium">{review.name}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-8">核心团队</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {teamMembers.map((member) => (
                  <div key={member.name} className="p-6 rounded-lg bg-background-50 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-user-line text-secondary-600 text-2xl w-8 h-8 flex items-center justify-center"></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900">{member.name}</h3>
                    <p className="text-xs text-primary-500 font-medium mt-1">{member.role}</p>
                    <p className="text-xs text-foreground-500 mt-2 leading-relaxed">{member.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="max-w-3xl mx-auto px-4 md:px-6 py-12">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-8">联系我们</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
              <div className="text-center p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-primary-500 text-xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3 className="font-semibold text-sm text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-500">contact@hanmanwang.com</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-chat-3-line text-accent-500 text-xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3 className="font-semibold text-sm text-foreground-900 mb-1">在线客服</h3>
                <p className="text-xs text-foreground-500">工作日 9:00-18:00</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-team-line text-secondary-500 text-xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3 className="font-semibold text-sm text-foreground-900 mb-1">商务合作</h3>
                <p className="text-xs text-foreground-500">business@hanmanwang.com</p>
              </div>
            </div>
            <div className="rounded-lg bg-background-100 p-6 md:p-8">
              <h3 className="font-heading text-xl text-foreground-950 mb-4">给我们留言</h3>
              <form className="space-y-4" action="https://readdy.ai/api/form/d9oog1qftmtnfcfh0b30" method="POST" data-readdy-form="">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1">姓名</label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1">邮箱</label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="about-subject" className="block text-xs font-medium text-foreground-700 mb-1">主题</label>
                  <input
                    id="about-subject"
                    name="subject"
                    type="text"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1">留言内容</label>
                  <textarea
                    id="about-message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入留言内容（最多500字）"
                  ></textarea>
                </div>
                <div className="form-phone-alt-wrap">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  发送留言
                </button>
              </form>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}